<template>
  <div
    v-if="visible && notice && notice.due_soon && noticeText"
    class="renewal-banner"
    :class="notice.expired ? 'is-expired' : 'is-warning'"
    role="alert"
  >
    <div class="renewal-banner__content">
      <i class="fas" :class="notice.expired ? 'fa-exclamation-circle' : 'fa-clock'"></i>
      <span class="renewal-banner__text">{{ noticeText }}</span>
      <span v-if="notice.renewal_amount" class="renewal-banner__amount">
        {{ $t('subscription.amountDue') }}: {{ formatMoney(notice.renewal_amount) }}
      </span>
    </div>
    <button class="renewal-banner__close" @click="dismiss" :aria-label="$t('common.close')">
      <i class="fas fa-times"></i>
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import api from '@/api';
import { subscriptionNoticeText } from '@/utils/subscriptionNotice';

const store = useStore();
const toast = useToast();
const { t } = useI18n();
const notice = ref(null);
const visible = ref(true);

const noticeText = computed(() => subscriptionNoticeText(notice.value));

const formatMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const dismiss = () => {
  visible.value = false;
};

onMounted(async () => {
  // Only show to school_admin — not teachers, students, guardians, etc.
  if (!store.getters.isAuthenticated || store.getters.isSuperAdmin) return;
  if (store.state.user?.role?.slug !== 'school_admin') return;

  try {
    const { data } = await api.get('/my-subscription');
    notice.value = data?.renewal_notice || null;

    const n = notice.value;
    if (n) {
      if (n.expired) {
        toast.error(t('subscription.toastExpired'), { timeout: 9000 });
      } else if (n.days_left != null && n.days_left >= 0 && n.days_left <= 3) {
        toast.warning(t('subscription.toastDaysLeft', { days: n.days_left }), { timeout: 9000 });
      }
    }
  } catch (e) {
    // Non-fatal: a 402 here is handled globally by the api interceptor.
    notice.value = null;
  }
});
</script>

<style scoped>
.renewal-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  margin-bottom: 1rem;
  font-size: 0.95rem;
}
.renewal-banner.is-warning {
  background: #fff8e1;
  color: #8a6d3b;
  border: 1px solid #ffe7a0;
}
.renewal-banner.is-expired {
  background: #fdecea;
  color: #a94442;
  border: 1px solid #f5c6cb;
}
.renewal-banner__content {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}
.renewal-banner__amount {
  font-weight: 700;
}
.renewal-banner__close {
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
}
.renewal-banner__close:hover {
  opacity: 1;
}
</style>
